import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import { CheckboxOption, Offset, Product } from '@uc/web/shared/data-models';
import { FilterApiService, SortBy, SortDir } from '@uc/web/shared/feature/feature-filter';
import {
	Events,
	EventsFilterOptions,
	EventsSearchQueryParams,
	EventsSearchQueryParamKey,
	EventsFilterPill,
	EventsSearchPayload,
} from '../../models/open-days.models';
import { OpenDaysApiService } from '../services/open-days-api.service';
import {
	DEFAULT_EVENTS_SEARCH_PAYLOAD,
	FILTER_OPTIONS,
} from '../../static/open-days-search.properties';
import { generatePayload } from '../../utils/payload.utils';
import {
	setCheckedValueForFilters,
	updateFiltersCount,
} from '../../utils/filter-options.utils';
import { updateQueryParams } from '../../utils/query-params.utils';
import { generateLink } from '../../utils/url.utils';
import { Location } from '@angular/common';
import { generatePills } from '../../utils/pills.utils';
import { ApolloError } from '@apollo/client/errors';

type EventsState = {
	queryParams: EventsSearchQueryParams;
	payload: EventsSearchPayload;
	events: Events | null;
	filters: EventsFilterOptions;
	pills: EventsFilterPill[];
	isLoading: boolean;
	isPaginationLoading: boolean;
	locationFiltersLoaded: boolean;
	error: string | null;
};

const initialState: EventsState = {
	queryParams: {} as EventsSearchQueryParams,
	payload: { ...DEFAULT_EVENTS_SEARCH_PAYLOAD },

	events: null,
	filters: {
		types: [...FILTER_OPTIONS.types],
		locations: [],
	},
	pills: [],
	isLoading: false,
	isPaginationLoading: false,
	locationFiltersLoaded: false,
	error: null,
};

export const EventsStore = signalStore(
	{ protectedState: false },
	withState(initialState),

	withMethods(
		(
			store,
			location = inject(Location),
			eventsApiSrv = inject(OpenDaysApiService),
			filterApiSrv = inject(FilterApiService),
		) => ({
			fetchEvents: rxMethod<void>(
				pipe(
					tap(() => {
						patchState(store, { isLoading: true, error: null });
						updateUrl(location, store.queryParams());
					}),
					switchMap(() =>
						eventsApiSrv.getEvents(store.payload()).pipe(
							tapResponse({
								next: (events: Events) =>
									patchState(store, {
										events: events,
										error: null,
										filters: updateFiltersCount(
											store.filters(),
											events.filters,
										),
									}),
								error: (error: ApolloError) =>
									patchState(store, { error: error.message }),
								finalize: () =>
									patchState(store, {
										isLoading: false,
										isPaginationLoading: false,
									}),
							}),
						),
					),
				),
			),

			fetchPaginatedEvents: rxMethod<void>(
				pipe(
					tap(() => {
						patchState(store, { isLoading: true, error: null });
						updateUrl(location, store.queryParams());
					}),
					switchMap(() =>
						eventsApiSrv.getEvents(store.payload()).pipe(
							tapResponse({
								next: (events: Events) =>
									patchState(store, {
										events: {
											...events,
											data: [
												...(store.events()?.data ?? []),
												...events.data,
											],
										},
										filters: updateFiltersCount(
											store.filters(),
											events.filters,
										),
										error: null,
									}),
								error: (error: ApolloError) =>
									patchState(store, { error: error.message }),
								finalize: () =>
									patchState(store, {
										isLoading: false,
										isPaginationLoading: false,
									}),
							}),
						),
					),
				),
			),

			fetchFilterLocations: rxMethod<void>(
				pipe(
					switchMap(() =>
						filterApiSrv.getLocations().pipe(
							tapResponse({
								next: (locations: CheckboxOption[]) => {
									patchState(store, {
										filters: { ...store.filters(), locations },
									});
								},
								error: (error: string) => {
									patchState(store, { error: error });
								},
								finalize: () => {
									patchState(store, {
										locationFiltersLoaded: true,
									});
								},
							}),
						),
					),
				),
			),

			updateProduct(product: Product) {
				const updatedQueryParams = {
					...store.queryParams(),
					level: product,
				};

				const data = formatStateData(
					store.payload(),
					updatedQueryParams,
					store.filters(),
				);

				patchState(store, {
					queryParams: updatedQueryParams,
					payload: data.payload,
					filters: data.filters,
					isLoading: data.isLoading,
				});
			},

			updateSearchTerm(searchTerm: string) {
				const updatedQueryParams = updateQueryParams(
					store.queryParams(),
					's',
					searchTerm,
				);

				const data = formatStateData(
					store.payload(),
					updatedQueryParams,
					store.filters(),
				);

				patchState(store, {
					queryParams: updatedQueryParams,
					payload: data.payload,
					filters: data.filters,
					isLoading: data.isLoading,
				});
			},

			updateCheckboxFilter(
				urlKey: EventsSearchQueryParamKey,
				urlValue: string,
				isChecked: boolean,
			) {
				const updatedQueryParams = updateQueryParams(
					store.queryParams(),
					urlKey,
					urlValue,
					isChecked,
				);

				const data = formatStateData(
					store.payload(),
					updatedQueryParams,
					store.filters(),
				);
				patchState(store, {
					queryParams: updatedQueryParams,
					payload: data.payload,
					filters: data.filters,
					pills: data.pills,
					isLoading: data.isLoading,
				});
			},

			updateCalendarFilter(dates: { dateFrom: string; dateTo: string }) {
				const urlValue = dates.dateTo
					? `${dates.dateFrom}_${dates.dateTo}`
					: dates.dateFrom;

				const updatedQueryParams = updateQueryParams(
					store.queryParams(),
					'date',
					urlValue,
					!!dates.dateFrom,
				);

				const data = formatStateData(
					store.payload(),
					updatedQueryParams,
					store.filters(),
				);

				patchState(store, {
					queryParams: updatedQueryParams,
					payload: {
						...store.payload(),
						dateFrom: dates.dateFrom || null,
						dateTo: dates.dateTo || null,
					},
					pills: data.pills,
				});
			},

			resetFilter() {
				const updatedQueryParams = {} as EventsSearchQueryParams;
				const data = formatStateData(
					store.payload(),
					updatedQueryParams,
					store.filters(),
				);
				patchState(store, {
					queryParams: updatedQueryParams,
					payload: data.payload,
					filters: data.filters,
					pills: data.pills,
					isLoading: data.isLoading,
				});
			},

			onSort(sortBy: SortBy, sortDir: SortDir) {
				const offset = store.payload().page > 1 ? Offset.Off : Offset.On;

				patchState(store, {
					payload: {
						...store.payload(),
						sortBy,
						sortDirection: sortDir,
						offset,
					},
				});
			},

			initQueryParams(queryParams: EventsSearchQueryParams) {
				let updatedQueryParams = {
					...store.queryParams(),
					...queryParams,
				};

				const data = formatStateData(
					store.payload(),
					updatedQueryParams,
					store.filters(),
					false,
				);

				//update page if query params on page refresh or accessed with url
				const page = data.payload.page;
				if (page > 1) {
					updatedQueryParams = {
						...updatedQueryParams,
						page: page.toString(),
					};
				}

				patchState(store, {
					queryParams: updatedQueryParams,
					payload: data.payload,
					filters: data.filters,
					pills: data.pills,
					isLoading: data.isLoading,
				});
			},

			onLoadMore(page: number) {
				const pageInt = Number.isInteger(page) ? page : 1;

				patchState(store, {
					queryParams: {
						...store.queryParams(),
						page: pageInt.toString(),
					},
					payload: {
						...store.payload(),
						offset: Offset.On,
						page: pageInt,
					},
					isPaginationLoading: true,
				});
			},

			resetStore() {
				patchState(store, initialState);
			},
		}),
	),
);

const formatStateData = (
	payload: EventsSearchPayload,
	queryParams: EventsSearchQueryParams,
	filters: EventsFilterOptions,
	resetPage = true,
) => {
	if (resetPage) delete queryParams.page;

	const updatedPayload = generatePayload(payload, queryParams, filters);

	//update checked values on all filters
	const updatedFilters = setCheckedValueForFilters(filters, updatedPayload);

	const pills = generatePills(updatedPayload, updatedFilters);

	return {
		payload: updatedPayload,
		filters: updatedFilters,
		pills: pills,
		isLoading: true,
	};
};

const updateUrl = (location: Location, queryParams: EventsSearchQueryParams) => {
	const currentUrl = location.path();
	const baseUrl = currentUrl.split('?')[0];
	const url = generateLink(baseUrl, queryParams);

	if (currentUrl !== url) {
		location.go(url);
	}
};
